import { forwardRef, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { ExternalLink, X } from 'lucide-react'
import { subscribersApi, type SubscriberDecision, type SubscriberRow } from '@/api/subscribers'
import { licensingApi, isPlatformAdminRequiredError } from '@/api/licensing'
import { LIMIT_KEYS, LIMIT_LABELS } from '@/types/licensing'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui'
import {
  DECISION_LABEL,
  DECISION_OPTIONS,
  STATUS_TONE,
  TONE_CLASS,
  ddmmyyyy,
  ddmmyyyyTime,
  moneyBreakdown,
  nis,
  periodLabel,
  sortedFlags,
  subscriberFlagLabel,
  subscriberFlagTone,
  type Tone,
} from './subscribersFormat'

export function Chip({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[11px] font-medium leading-none ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  )
}

const PERIOD_TONE: Record<string, Tone> = {
  paid: 'green',
  future: 'gray',
  unpaid: 'red',
  failed: 'red',
  partial: 'amber',
}

const FLAG_HELP: Record<string, string> = {
  in_grace: 'No saved card on the charge date. Service remains active during the seven-day grace window.',
  no_card: 'Active and billed, but no card on file — the next charge cannot run.',
  unpaid: 'A billing period is unpaid or failed.',
  partial: 'A period was charged only partly — there is a remaining balance.',
  needs_review: 'Billing run marked for manual review, or the first charge failed.',
  unknown: 'The current period has no clear paid/unpaid status.',
  trial_ending: 'Trial ends within 7 days.',
  expired_in_use: 'Subscription expired but the org is still in use.',
  trial_no_card: 'Trial ends within 14 days and no credit card is on file — they will not convert automatically.',
  renewal_soon: 'Next charge is within 14 days.',
  not_linked: 'No Otomator billing account for this org yet — a card cannot be saved or checked until it is linked.',
  mismatch: 'JumpStart is active/trial while the WhatsApp subscription is expired, cancelled or suspended.',
}

export function useSaveDecision(persistence: 'rpc' | 'local') {
  const qc = useQueryClient()
  const author = useAuthStore((s) => s.user?.email ?? null)
  return useMutation({
    mutationFn: (v: { orgId: string; decision: SubscriberDecision; note: string }) =>
      subscribersApi.saveDecision(v.orgId, v.decision, v.note, author),
    onSuccess: (res) => {
      toast.success(res.persistence === 'rpc' ? 'Decision saved' : 'Decision saved on this browser (migration not applied yet)')
      void qc.invalidateQueries({ queryKey: ['subscribers-control-center'] })
    },
    onError: (e: Error) => toast.error(e.message || 'Could not save decision'),
    meta: { persistence },
  })
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-navy-border px-5 py-4">
      <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink-muted">{title}</h3>
      {children}
    </section>
  )
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-xs">
      <span className="text-ink-secondary">{k}</span>
      <span className="num text-right text-ink-primary">{v}</span>
    </div>
  )
}

interface PanelProps {
  row: SubscriberRow
  persistence: 'rpc' | 'local'
  onClose: () => void
  startExtendTrial?: boolean
}

function isTrialStatus(status: string): boolean {
  return status === 'trial' || status === 'trialing'
}

function dateInputValue(date: Date): string {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function endOfDate(value: string): Date | null {
  if (!value) return null
  const date = new Date(`${value}T23:59:59.999`)
  return Number.isNaN(date.getTime()) ? null : date
}

const SubscriberPanel = forwardRef<HTMLButtonElement, PanelProps>(function SubscriberPanel(
  { row, persistence, onClose, startExtendTrial = false },
  decisionRef,
) {
  const [decision, setDecision] = useState<SubscriberDecision>(row.decision)
  const [note, setNote] = useState(row.decision_note)
  const [extensionDays, setExtensionDays] = useState<number | null>(null)
  const [extensionDate, setExtensionDate] = useState('')
  const qc = useQueryClient()
  const author = useAuthStore((s) => s.user?.email ?? null)
  useEffect(() => {
    setDecision(row.decision)
    setNote(row.decision_note)
  }, [row.organization_id, row.decision, row.decision_note])
  useEffect(() => {
    setExtensionDays(null)
    setExtensionDate('')
    if (startExtendTrial) setDecision('extend_trial')
  }, [row.organization_id, startExtendTrial])

  const save = useSaveDecision(persistence)
  const dirty = decision !== row.decision || note !== row.decision_note
  const isTrial = isTrialStatus(row.status)
  const currentEnd = useMemo(() => {
    const parsed = row.trial_end ? new Date(row.trial_end) : null
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null
  }, [row.trial_end])
  const extensionUntil = useMemo(() => {
    if (extensionDays != null) {
      const base = currentEnd && currentEnd.getTime() > Date.now() ? currentEnd : new Date()
      return new Date(base.getTime() + extensionDays * 24 * 60 * 60 * 1000)
    }
    return endOfDate(extensionDate)
  }, [currentEnd, extensionDate, extensionDays])
  const minimumExtensionDate = useMemo(() => {
    const base = currentEnd && currentEnd.getTime() > Date.now() ? new Date(currentEnd) : new Date()
    base.setDate(base.getDate() + 1)
    return dateInputValue(base)
  }, [currentEnd])

  const history = useQuery({
    queryKey: ['subscriber-billing-history', row.organization_id],
    queryFn: () => subscribersApi.billingHistory(row.organization_id),
    staleTime: 60_000,
  })
  const limits = useQuery({
    queryKey: ['subscriber-limits', row.organization_id],
    queryFn: () => licensingApi.getEffectiveLimits(row.organization_id),
    staleTime: 60_000,
    retry: false,
  })
  const extensions = useQuery({
    queryKey: ['subscriber-trial-extensions', row.organization_id],
    queryFn: () => subscribersApi.trialExtensionHistory(row.organization_id),
    staleTime: 30_000,
    retry: false,
  })
  const log = subscribersApi.decisionLog(row.organization_id)

  const extensionSave = useMutation({
    mutationFn: async () => {
      if (!isTrial || !extensionUntil) throw new Error('Choose a trial extension length')
      const extension = await subscribersApi.extendTrial(row.organization_id, {
        days: extensionDays,
        until: extensionDays == null ? extensionUntil.toISOString() : null,
        note,
      })
      const prefix = `Extend trial · ${extensionDays == null ? 'custom date' : `${extensionDays}d`} · until ${ddmmyyyy(extension.new_end)}`
      const decisionNote = note.trim() ? `${prefix} · ${note.trim()}` : prefix
      await subscribersApi.saveDecision(row.organization_id, 'extend_trial', decisionNote, author)
      return { extension, decisionNote }
    },
    onSuccess: ({ decisionNote }) => {
      setDecision('extend_trial')
      setNote(decisionNote)
      setExtensionDays(null)
      setExtensionDate('')
      toast.success('Trial extended and decision saved')
      void qc.invalidateQueries({ queryKey: ['subscribers-control-center'] })
      void qc.invalidateQueries({ queryKey: ['subscriber-trial-extensions', row.organization_id] })
    },
    onError: (e: Error) => toast.error(e.message || 'Could not extend trial'),
  })

  const canSubmit = decision === 'extend_trial'
    ? isTrial && extensionUntil != null
    : dirty
  const submit = () => {
    if (!canSubmit) return
    if (decision === 'extend_trial') extensionSave.mutate()
    else save.mutate({ orgId: row.organization_id, decision, note })
  }

  return (
    <aside
      aria-label={`Details for ${row.org_name}`}
      data-testid="subscriber-panel"
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[460px] flex-col border-l border-navy-border bg-navy-deeper shadow-modal"
    >
      <header className="flex items-start justify-between gap-3 px-5 py-4">
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold text-ink-primary" dir="auto">{row.org_name}</div>
          <div className="truncate text-xs text-ink-secondary">
            {row.owner_name ? <bdi>{row.owner_name}</bdi> : null}
            {row.owner_name && row.owner_email ? ' · ' : ''}
            {row.owner_email ?? (row.owner_name ? '' : '—')}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Chip tone="gray"><bdi>{row.plan_name}</bdi></Chip>
            <Chip tone={STATUS_TONE[row.status] ?? 'gray'}>{row.status}</Chip>
            {sortedFlags(row.flags).map((f) => (
              <Chip key={f} tone={subscriberFlagTone(f, row)} title={FLAG_HELP[f]}>{subscriberFlagLabel(f, row)}</Chip>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-ink-secondary hover:bg-navy-hover hover:text-ink-primary"
          aria-label="Close panel (Esc)"
          title="Close (Esc)"
        >
          <X size={18} />
        </button>
      </header>

      <div className="flex gap-3 px-5 pb-3 text-xs">
        <Link to={`/orgs/${row.organization_id}`} className="inline-flex items-center gap-1 text-gold hover:underline">
          Org admin page <ExternalLink size={12} />
        </Link>
        <Link to={`/orgs/${row.organization_id}?tab=billing`} className="inline-flex items-center gap-1 text-gold hover:underline">
          Billing panel <ExternalLink size={12} />
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Section title="Decision">
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Decision" data-testid="panel-decision">
              {DECISION_OPTIONS.filter((o) => o.value !== 'none').map((o, i) => {
                const on = decision === o.value
                const disabled = o.value === 'extend_trial' && !isTrial
                return (
                  <button
                    key={o.value}
                    ref={i === 0 ? decisionRef : undefined}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={disabled}
                    title={disabled ? 'not in trial' : undefined}
                    onClick={() => {
                      setDecision(on ? 'none' : o.value)
                      if (o.value !== 'extend_trial' || on) {
                        setExtensionDays(null)
                        setExtensionDate('')
                      }
                    }}
                    className={`rounded-md border px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gold ${
                      disabled
                        ? 'cursor-not-allowed border-navy-border text-ink-muted opacity-50'
                        : on ? 'border-gold bg-gold-dim text-gold' : 'border-navy-border text-ink-secondary hover:bg-navy-hover'
                    }`}
                  >
                    {o.key} · {o.label}
                  </button>
                )
              })}
            </div>
            {!isTrial && <div className="text-[11px] text-ink-muted">Extend trial: not in trial</div>}
            {decision === 'extend_trial' && isTrial && (
              <div className="space-y-2 rounded-md border border-gold/30 bg-gold-dim/40 p-3" data-testid="extend-trial-controls">
                <div className="text-xs font-medium text-ink-primary">Choose the extension length</div>
                <div className="flex flex-wrap gap-1.5">
                  {[7, 14, 30].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => { setExtensionDays(days); setExtensionDate('') }}
                      className={`rounded border px-2.5 py-1 text-xs ${extensionDays === days ? 'border-gold bg-gold-dim text-gold' : 'border-navy-border text-ink-secondary hover:bg-navy-hover'}`}
                    >
                      {days} days
                    </button>
                  ))}
                  <input
                    type="date"
                    aria-label="Extend trial until"
                    min={minimumExtensionDate}
                    value={extensionDate}
                    onChange={(e) => { setExtensionDate(e.target.value); setExtensionDays(null) }}
                    className="rounded border border-navy-border bg-navy-card px-2 py-1 text-xs text-ink-primary focus:border-gold focus:outline-none"
                  />
                </div>
                <div className="num text-xs text-ink-secondary">
                  current end {ddmmyyyy(row.trial_end)} → new end {extensionUntil ? ddmmyyyy(extensionUntil.toISOString()) : '—'}
                </div>
                {extensionSave.error && <div className="text-xs text-danger">{(extensionSave.error as Error).message}</div>}
              </div>
            )}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault()
                  submit()
                }
              }}
              rows={3}
              dir="auto"
              placeholder="Note (Ctrl+Enter to save)"
              data-testid="panel-note"
              className="w-full rounded border border-navy-border bg-navy-card px-2 py-1.5 text-sm text-ink-primary placeholder:text-ink-muted focus:border-gold focus:outline-none"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-ink-muted">
                {row.decided_at
                  ? <>Last: <b className="text-ink-secondary">{DECISION_LABEL[row.decision]}</b> · {ddmmyyyyTime(row.decided_at)}{row.decided_by ? ` · ${row.decided_by}` : ''}</>
                  : 'No decision recorded yet'}
              </span>
              <Button type="button" size="sm" loading={save.isPending || extensionSave.isPending} disabled={!canSubmit} onClick={submit}>
                Save
              </Button>
            </div>
          </div>
        </Section>

        <Section title="Money (monthly)">
          <div className="num mb-2 text-sm text-ink-primary" data-testid="panel-money">{moneyBreakdown(row)}</div>
          <KV k="Net (before VAT)" v={nis(row.net_agorot)} />
          <KV k={`VAT ${row.vat_percent}%`} v={`+ ${nis(row.vat_agorot)}`} />
          <KV k="Total incl. VAT" v={<b>{nis(row.gross_agorot)}</b>} />
          {row.benefit_label && <KV k="Benefit" v={<bdi className="text-success">{row.benefit_label}</bdi>} />}
          <KV k="Billing cycle" v={row.billing_cycle ?? '—'} />
          <KV k="Card" v={!row.billing_linked ? <span className="text-ink-muted">Not linked to billing</span> : row.card_last4 ? `${row.card_brand ? `${row.card_brand} ` : ''}•••• ${row.card_last4}` : <span className="text-danger">No card</span>} />
          <KV k="Open balance" v={<span className={row.open_balance_agorot > 0 ? 'text-danger' : ''}>{nis(row.open_balance_agorot)}</span>} />
          <KV k="Next charge" v={ddmmyyyy(row.next_charge_at)} />
          <KV k="Trial end" v={ddmmyyyy(row.trial_end)} />
        </Section>

        <Section title="First charge">
          <KV k="Status" v={row.first_charge_status ?? '—'} />
          <KV k="Charge date" v={ddmmyyyy(row.first_charge_at)} />
          <KV k="Period" v={periodLabel(row.first_charge_period_key)} />
          <KV k="Amount (net)" v={nis(row.first_charge_amount_agorot)} />
        </Section>

        <Section title="Billing history">
          {history.isLoading ? (
            <div className="text-xs text-ink-muted">Loading billing history…</div>
          ) : history.error ? (
            <div className="text-xs text-danger">Could not load billing history: {(history.error as Error).message}</div>
          ) : !history.data?.length ? (
            <div className="text-xs text-ink-muted">No billing periods yet.</div>
          ) : (
            <table className="w-full text-xs" data-testid="panel-history">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-ink-muted">
                  <th className="py-1 font-medium">Period</th>
                  <th className="py-1 font-medium">Total (net + VAT)</th>
                  <th className="py-1 font-medium">Status</th>
                  <th className="py-1 text-right font-medium">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((p) => (
                  <tr key={p.period_key} className="border-t border-navy-border/50">
                    <td className="num py-1.5 text-ink-primary">{periodLabel(p.period_key)}</td>
                    <td className="num py-1.5 text-ink-primary" title={`${nis(p.net_agorot)} + ${nis(p.vat_agorot)} VAT`}>
                      {nis(p.gross_agorot)} <span className="text-ink-muted">({nis(p.net_agorot)} + VAT)</span>
                    </td>
                    <td className="py-1.5"><Chip tone={PERIOD_TONE[p.status] ?? 'gray'}>{p.status}</Chip></td>
                    <td className="num py-1.5 text-right">
                      {p.document_url ? (
                        <a href={p.document_url} target="_blank" rel="noreferrer" className="text-gold hover:underline">
                          {p.sumit_document_number ?? 'Open'}
                        </a>
                      ) : (p.sumit_document_number ?? '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {row.last_charge_at && (
            <div className="mt-2 text-[11px] text-ink-secondary">
              Last successful charge {ddmmyyyy(row.last_charge_at)} · {nis(row.last_charge_amount_agorot)}
              {row.last_charge_invoice_numbers ? ` · #${row.last_charge_invoice_numbers}` : ''}
            </div>
          )}
        </Section>

        <Section title="Licensing — usage vs plan">
          {limits.isLoading ? (
            <div className="text-xs text-ink-muted">Loading limits…</div>
          ) : limits.error ? (
            <div className="text-xs text-ink-muted">
              {isPlatformAdminRequiredError(limits.error) ? 'Limits need a platform-admin session.' : `Could not load limits: ${(limits.error as Error).message}`}
            </div>
          ) : (
            <div className="space-y-1">
              {LIMIT_KEYS.map((k) => {
                const s = limits.data?.[k]
                if (!s || (!s.effective && !s.used)) return null
                const over = s.effective > 0 && s.used > s.effective
                const pct = s.effective > 0 ? Math.min(100, Math.round((s.used / s.effective) * 100)) : 0
                return (
                  <div key={k}>
                    <KV
                      k={LIMIT_LABELS[k].label}
                      v={<span className={over ? 'text-danger' : ''}>{s.used.toLocaleString('en-US')} / {s.effective.toLocaleString('en-US')}{LIMIT_LABELS[k].unit ? ` ${LIMIT_LABELS[k].unit}` : ''}</span>}
                    />
                    <div className="h-1.5 rounded bg-navy-border">
                      <div className={`h-full rounded ${over ? 'bg-danger' : pct >= 90 ? 'bg-warning' : 'bg-success'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Section>

        <Section title="Mismatches">
          {row.flags.length === 0 && !row.wa_status ? (
            <div className="text-xs text-ink-muted">None.</div>
          ) : (
            <ul className="space-y-1 text-xs text-ink-secondary">
              {sortedFlags(row.flags).map((f) => (
                <li key={f}><Chip tone={subscriberFlagTone(f, row)}>{subscriberFlagLabel(f, row)}</Chip> <span className="ml-1">{FLAG_HELP[f]}</span></li>
              ))}
              {row.wa_status && <li>WhatsApp subscription: <b className="text-ink-primary">{row.wa_status}</b></li>}
              {row.period_status && <li>Current period: <b className="text-ink-primary">{row.period_status}</b></li>}
            </ul>
          )}
        </Section>

        <Section title="Decision history">
          {extensions.isLoading ? (
            <div className="text-xs text-ink-muted">Loading trial extensions…</div>
          ) : extensions.error ? (
            <div className="mb-2 text-xs text-danger">Could not load trial extensions: {(extensions.error as Error).message}</div>
          ) : extensions.data?.length ? (
            <ul className="mb-3 space-y-1.5 text-xs" data-testid="trial-extension-history">
              {extensions.data.map((e) => (
                <li key={e.id} className="text-ink-secondary">
                  <span className="num text-ink-muted">{ddmmyyyyTime(e.created_at)}</span>{' '}
                  <b className="text-ink-primary">Trial extended</b>
                  {e.extended_by_email ? <span className="text-ink-muted"> · {e.extended_by_email}</span> : null}
                  <div className="num mt-0.5">{ddmmyyyy(e.previous_end)} → {ddmmyyyy(e.new_end)}</div>
                </li>
              ))}
            </ul>
          ) : null}
          {log.length === 0 && !extensions.data?.length ? (
            <div className="text-xs text-ink-muted">No decision or trial-extension history recorded.</div>
          ) : log.length ? (
            <ul className="space-y-1.5 text-xs">
              {log.slice(0, 20).map((e) => (
                <li key={e.decided_at} className="text-ink-secondary">
                  <span className="num text-ink-muted">{ddmmyyyyTime(e.decided_at)}</span>{' '}
                  <b className="text-ink-primary">{DECISION_LABEL[e.decision]}</b>
                  {e.decided_by ? <span className="text-ink-muted"> · {e.decided_by}</span> : null}
                  {e.note ? <div dir="auto" className="mt-0.5 text-ink-secondary">{e.note}</div> : null}
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
      </div>
    </aside>
  )
})

export default SubscriberPanel
